import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { z } from 'zod';

import { Button } from '@repo/components/button';
import { Card } from '@repo/components/card';
import { LoadingIndicator } from '@repo/components/loading-indicator';
import { SearchField } from '@repo/components/search-field';
import { Select, SelectItem } from '@repo/components/select';
import { Table } from '@repo/components/table';

import { formatDate } from '@repo/utils/format-date';
import { pluralize } from '@repo/utils/pluralize';
import { useNumberFormatter } from '@repo/utils/use-number-formatter';

import { ContentHeader } from '~/features/content-header/content-header.tsx';
import { queryInvoices } from '~/services/invoice/invoice.ts';

import { invoicePeriodLabel, invoiceTypeLabel } from './-utils.ts';

const searchSchema = z.object({
  year: z.number().int().optional().catch(undefined),
  type: z.string().optional().catch(undefined),
  q: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/project/$project/(invoices)/invoices/')({
  validateSearch: searchSchema.catch(() => searchSchema.parse({})),
  component: RouteInvoices,
  loader: async ({ context: { queryClient }, params: { project } }) => {
    await queryClient.ensureQueryData(queryInvoices(project));
  },
});

function RouteInvoices() {
  const { project } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data, isPending, isError, refetch, isFetching } = useQuery(queryInvoices(project));
  const money = useNumberFormatter({ minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const setSearch = (patch: { year?: number | undefined; type?: string | undefined; q?: string | undefined }) => {
    void navigate({ to: '.', search: prev => ({ ...prev, ...patch }), replace: true, resetScroll: false });
  };

  const all = data ?? [];
  const years = [...new Set(all.map(invoice => Number(invoice.periodStart.slice(0, 4))))].sort((a, b) => b - a);
  const types = [...new Set(all.map(invoice => invoice.type))];
  const needle = (search.q ?? '').trim().toLocaleLowerCase();
  const invoices = all.filter(invoice =>
    (search.year == null || Number(invoice.periodStart.slice(0, 4)) === search.year)
    && (search.type == null || invoice.type === search.type)
    && (needle === '' || (invoice.cargo ?? '').toLocaleLowerCase().includes(needle)));
  const filtered = search.year != null || search.type != null || needle !== '';

  return (
    <div className="flex flex-col items-start gap-6 pb-6 max-w-7xl mx-auto w-full">
      <div className="space-y-1 pb-4 w-full border-b border-b-secondary-200">
        <ContentHeader>Invoices</ContentHeader>
        <div className="flex items-center gap-2">
          <p>{isPending ? 'Loading invoices' : isError ? 'Invoices unavailable' : pluralize(invoices.length, 'invoice')}</p>
          {isFetching && !isPending && <LoadingIndicator size={16} className="text-primary-700" />}
        </div>
      </div>

      <div className="grid w-full sm:flex sm:flex-wrap items-end gap-4">
        <SearchField
          aria-label="Find a cargo"
          placeholder="Cargo"
          value={search.q ?? ''}
          onChange={(value) => { setSearch({ q: value || undefined }); }}
        />
        <Select
          aria-label="Year"
          value={search.year == null ? 'all' : String(search.year)}
          items={[{ id: 'all', name: 'All years' }, ...years.map(year => ({ id: String(year), name: String(year) }))]}
          onChange={(key) => { setSearch({ year: key === 'all' ? undefined : Number(key) }); }}
        >
          {item => <SelectItem id={item.id}>{item.name}</SelectItem>}
        </Select>
        <Select
          aria-label="Type"
          value={search.type ?? 'all'}
          items={[{ id: 'all', name: 'All types' }, ...types.map(type => ({ id: type, name: invoiceTypeLabel(type) }))]}
          onChange={(key) => { setSearch({ type: key === 'all' ? undefined : String(key) }); }}
        >
          {item => <SelectItem id={item.id}>{item.name}</SelectItem>}
        </Select>
      </div>

      <Card className="p-0 gap-0 w-full overflow-hidden">
        {isPending && (
          <div className="px-6 py-12 flex justify-center"><LoadingIndicator size={24} className="text-primary-700" /></div>
        )}
        {isError && (
          <div className="px-6 py-8 text-center text-secondary-500">
            The invoices could not be loaded.
            <Button variant="ghost" size="sm" action={() => { void refetch(); }} className="ml-2">Try again</Button>
          </div>
        )}
        {!isPending && !isError && invoices.length === 0 && (
          <div className="px-6 py-8 text-center text-secondary-500 flex flex-col items-center gap-3">
            <span>{filtered ? 'No invoices match these filters.' : 'No invoices yet.'}</span>
            {filtered && <Button variant="ghost" size="sm" action={() => { setSearch({ year: undefined, type: undefined, q: undefined }); }}>Clear filters</Button>}
          </div>
        )}
        {!isPending && !isError && invoices.length > 0 && (
          <div className="overflow-x-auto">
            <Table.Root>
              <Table.Table>
                <Table.Header>
                  <Table.Row disableHover>
                    <Table.HeaderCell>Period</Table.HeaderCell>
                    <Table.HeaderCell>Type</Table.HeaderCell>
                    <Table.HeaderCell>Cargo</Table.HeaderCell>
                    <Table.HeaderCell>Approved</Table.HeaderCell>
                    <Table.HeaderCell numeric>Amount</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {invoices.map(invoice => (
                    <Table.Row key={invoice.id}>
                      <Table.Cell className="whitespace-nowrap">
                        <Link to="./$invoice" params={{ invoice: invoice.id }} from={Route.fullPath} className="font-medium text-primary-800 hover:underline underline-offset-4">
                          {invoicePeriodLabel(invoice.periodStart, invoice.periodEnd)}
                        </Link>
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap">{invoiceTypeLabel(invoice.type)}</Table.Cell>
                      <Table.Cell className="whitespace-nowrap">{invoice.cargo ?? ''}</Table.Cell>
                      <Table.Cell className="whitespace-nowrap">{invoice.approvedOn ? formatDate(invoice.approvedOn, 'd MMM yyyy') : ''}</Table.Cell>
                      <Table.Cell numeric className="whitespace-nowrap tabular-nums">{`${money.format(invoice.total)} ${invoice.currency}`}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Table>
            </Table.Root>
          </div>
        )}
      </Card>
    </div>
  );
}
